import { Clock, CalendarDays, Eye, Mail, Star, UserPlus, Trophy, Heart } from "lucide-react";
import { toast } from "sonner";
import { useProfileStats } from "@/hooks/useProfileStats";

/** Statut « en ligne » : pastille verte pulsante. */
export const OnlineStatus = ({ enLigne, derniereFois }) => (
  <span className="inline-flex items-center gap-1.5" data-testid="statut-en-ligne">
    <span className="relative flex h-2.5 w-2.5">
      <span className={`h-2.5 w-2.5 rounded-full ${enLigne ? "bg-[#0A5C36]" : "bg-stone-400"}`} />
      {enLigne && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0A5C36] opacity-70" />}
    </span>
    <span className={`text-xs font-bold ${enLigne ? "text-[#0A5C36]" : "text-stone-500"}`}>
      {enLigne ? "en ligne" : depuis(derniereFois)}
    </span>
  </span>
);

const depuis = (iso) => {
  if (!iso) return "jamais";
  const minutes = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 1440) return `il y a ${Math.floor(minutes / 60)} h`;
  if (minutes < 2880) return "hier";
  if (minutes < 10080) return `il y a ${Math.floor(minutes / 1440)} jours`;
  return new Date(iso).toLocaleDateString("fr-FR");
};

const heure = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";

const jourLong = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";

const nombre = (n = 0) =>
  n >= 1000000 ? `${(n / 1000000).toFixed(2)} M` : n.toLocaleString("fr-FR");

const ACTIONS = [
  { type: "message", libelle: "Message", Icone: Mail, couleur: "#0A5C36" },
  { type: "favori", libelle: "Favoris", Icone: Star, couleur: "#D4AF37" },
  { type: "abonnement", libelle: "Suis-moi", Icone: UserPlus, couleur: "#4C6EF5", badge: true },
  { type: "jaime", libelle: "J'aime", Icone: Heart, couleur: "#E05638" },
];

/** Barre de statistiques de profil (LaGo, ClubVIP, Air-Bengue). */
export const ProfileStatsBar = ({ profileId, categorie, viewerId, onMessage, inscritLe }) => {
  const { stats, chargement, action, basculer } = useProfileStats(profileId, categorie, viewerId);

  if (chargement) {
    return <div className="h-28 animate-pulse rounded-2xl bg-stone-100" data-testid="stats-chargement" />;
  }
  if (!stats) return null;

  const agir = async (type) => {
    if (!viewerId) return toast.error("Connectez-vous pour interagir avec ce profil.");
    if (viewerId === profileId) return toast.error("C'est votre propre profil.");
    try {
      const r = await basculer(type);
      if (type === "message") {
        onMessage?.();
        return;
      }
      toast.success(r.actif ? `Ajouté : ${ACTIONS.find((a) => a.type === type).libelle}` : "Retiré");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || "Action impossible pour le moment.");
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-[#E5DFD5] bg-white"
      data-testid="profil-stats-bar">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#F0EBE3] px-5 py-4">
        <div className="space-y-1.5 text-sm">
          <p className="flex flex-wrap items-center gap-2 text-stone-600">
            <Clock size={14} className="text-stone-400" /> La dernière fois :
            <span className="font-semibold text-stone-900" data-testid="stats-derniere-fois">
              {stats.en_ligne
                ? `aujourd'hui, ${heure(new Date().toISOString())}`
                : depuis(stats.derniere_fois || inscritLe)}
            </span>
            <OnlineStatus enLigne={stats.en_ligne} derniereFois={stats.derniere_fois || inscritLe} />
          </p>
          <p className="flex items-center gap-2 text-stone-600">
            <CalendarDays size={14} className="text-stone-400" /> Date de l'enregistrement :
            <span className="font-semibold text-stone-900" data-testid="stats-inscrit-le">
              {jourLong(inscritLe || stats.inscrit_le)}
            </span>
          </p>
        </div>

        <div className="text-right">
          <p className="inline-flex items-center gap-2 text-stone-600">
            <Eye size={16} className="text-stone-400" /> Vues en total :
            <span className="font-heading text-xl font-extrabold text-stone-900" data-testid="stats-vues-total">
              {nombre(stats.vues_total)}
            </span>
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Vues aujourd'hui : <b className="text-stone-700" data-testid="stats-vues-jour">{stats.vues_jour}</b>
            {" · "}Vues uniques aujourd'hui :{" "}
            <b className="text-stone-700" data-testid="stats-vues-uniques">{stats.vues_uniques_jour}</b>
          </p>
        </div>
      </div>

      <div className="flex items-stretch gap-2 px-4 py-3">
        {ACTIONS.map(({ type, libelle, Icone, couleur, badge }) => {
          const actif = Boolean(stats.mes_interactions?.[type]);
          const compte = stats.interactions?.[type] || 0;
          return (
            <button key={type} onClick={() => agir(type)} disabled={action === type}
              data-testid={`stats-action-${type}`} title={libelle}
              className="relative flex flex-1 flex-col items-center gap-1 rounded-2xl py-2.5 transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
              style={{ backgroundColor: actif ? `${couleur}1f` : "#FAF8F5", color: actif ? couleur : "#78716C" }}>
              <Icone size={19} fill={actif && type !== "message" ? couleur : "none"} />
              <span className="text-[11px] font-bold leading-none">{libelle}</span>
              {badge && compte > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-[#E05638] px-1.5 py-0.5 text-[10px] font-bold text-white"
                  data-testid={`stats-badge-${type}`}>
                  {compte > 99 ? "99+" : compte}
                </span>
              )}
              {!badge && compte > 0 && (
                <span className="text-[10px] font-semibold opacity-70">{nombre(compte)}</span>
              )}
            </button>
          );
        })}

        <div className="flex flex-col items-center justify-center gap-1 rounded-2xl px-4 py-2.5"
          data-testid="stats-top50"
          style={stats.top50
            ? { background: "linear-gradient(135deg,#D4AF37,#E05638)", color: "#fff" }
            : { backgroundColor: "#FAF8F5", color: "#A8A29E" }}>
          <Trophy size={19} />
          <span className="text-[11px] font-bold leading-none">{stats.top50 ? "TOP 50" : "Classement"}</span>
        </div>
      </div>
    </section>
  );
};
